<template>
    <div>
        <b-list-group-item
            class="text-block"
            :class="[editable && 'edit-modus', dynamicClass()]"
            active-class="active">
            <div
                class="duration"
                v-if="textBlockIn.duration">
                <span class="time">{{ textBlockIn.duration }}</span>
                <span class="unit">min</span>
            </div>
            <div class="text-block-header">
                <div class="text-block-title-wrapper">
                    <b-button
                        class="btn-drag-icon btn-sm"
                        v-if="editable"
                        variant="none"
                        @click="$event.stopPropagation()">
                        <i class="fas fa-fw fa-arrows-alt-v text-block-handle"></i>
                    </b-button>
                    <h3 class="text-block-title">{{ textBlockIn.title }}</h3>
                </div>

                <div
                    class="text-block-actions"
                    v-if="editable">
                    <b-button
                        class="btn-edit"
                        v-b-tooltip.hover
                        title="Bewerken"
                        variant="primary-icon-only"
                        @click="openEditModal">
                    </b-button>

                    <b-button
                        class="btn-chalkboard-teacher"
                        v-b-tooltip.hover
                        :title="$t('lesson.add_slide')"
                        variant="primary-icon-only"
                        @click="addSlide">
                    </b-button>

                    <b-button
                        class="btn-remove"
                        v-b-tooltip.hover
                        :title="$t('common.remove')"
                        variant="primary-icon-only"
                        @click="deleteTextBlock">
                    </b-button>
                </div>
                <div
                    class="text-block-actions"
                    v-else>
                    <b-button
                        class="btn-chalkboard-teacher"
                        variant="primary-icon-only"
                        @click="addSlide">
                    </b-button>
                </div>
            </div>

            <div class="text-block-content">
                <design-2021-apprentice-display-html-component :html-content="textBlockIn.description">
                </design-2021-apprentice-display-html-component>
                <b-list-group
                    class="slide-group"
                    v-if="textBlockIn.step"
                    :id="`slides-${textBlockIn.id}`">
                    <draggable
                        :list="textBlockIn.step.slides"
                        group="slides"
                        handle=".slide-drag-handle"
                        ghost-class="ghost"
                        @change="slideOrderChanged">
                        <b-list-group-item
                            class="slide"
                            v-for="slide in textBlockIn.step.slides"
                            :key="slide.id">
                            <div class="header">
                                <span v-if="slide.editable">
                                    <i class="fas fa-fw fa-arrows-alt-v slide-drag-handle"></i>
                                </span>

                                <div class="actions">
                                    <b-button
                                        class="btn-play"
                                        v-b-tooltip.hover
                                        :title="$t('presentation.edit.play_slide')"
                                        variant="primary-icon-only"
                                        @click="openSlidePage(slide)">
                                    </b-button>
                                    <b-button
                                        class="btn-edit"
                                        v-if="editable || slide.editable"
                                        v-b-tooltip.hover
                                        :title="$t('common.edit')"
                                        variant="primary-icon-only"
                                        @click="openEditSlideModal(slide)">
                                    </b-button>
                                    <b-button
                                        class="btn-remove"
                                        v-if="editable || slide.editable"
                                        v-b-tooltip.hover
                                        :title="$t('common.remove')"
                                        variant="danger-icon-only"
                                        @click="deleteSlide(slide)">
                                    </b-button>
                                </div>
                            </div>

                            <design-2021-apprentice-slide-preview-component
                                @lesson-updated="lessonUpdated"
                                @open-edit-slide-modal="openEditSlideModal"
                                :text-block-id-in="textBlockIn.id"
                                :lesson-id-in="lessonIn.id"
                                :slide-in="slide">
                            </design-2021-apprentice-slide-preview-component>

                            <div class="slide-count">
                                {{
                                    $t('presentation.slide_number_out_of_total', {
                                        number: slideNumber(slide),
                                        total: totalSlides
                                    })
                                }}
                            </div>
                        </b-list-group-item>
                    </draggable>
                </b-list-group>
            </div>
        </b-list-group-item>
    </div>
</template>

<script>
    import draggable from 'vuedraggable';

    export default {
        name: 'design-2021-lesson-manual-single-text-block-component',
        components: { draggable },
        props: {
            lessonIn: {
                type: Object
            },
            textBlockIn: {
                type: Object
            },
            editable: {
                type: Boolean,
                required: false,
                default: false
            }
        },
        computed: {
            totalSlides() {
                return this.lessonIn.text_blocks.reduce((totalSlides, textBlock) => {
                    return totalSlides + (textBlock.step?.slides?.length || 0);
                }, 0);
            }
        },
        methods: {
            slideNumber(slide) {
                let currentSlideNumber = 0;

                for (const textBlock of this.lessonIn.text_blocks) {
                    for (const currentSlide of textBlock.step?.slides || []) {
                        currentSlideNumber++;

                        if (currentSlide === slide) {
                            return currentSlideNumber;
                        }
                    }
                }
            },
            slideOrderChanged() {
                let blocks = [];

                for (const textBlock of this.lessonIn.text_blocks) {
                    let block = {
                        id: textBlock.id,
                        slides: []
                    };
                    let x = 1;
                    for (const slide of textBlock.step.slides) {
                        block.slides.push({
                            id: slide.id,
                            order: x
                        });
                        x++;
                    }

                    blocks.push(block);
                }

                const data = {
                    blocks: blocks
                };
                this.axios.post(this.routes.lessonSlideOrder(this.lessonIn.id), data).then(
                    (response) => {
                        this.$emit('lesson-updated', response.data);
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    }
                );
            },
            openEditModal() {
                this.$emit('open-edit-modal', this.textBlockIn);
            },
            lessonUpdated(newLesson) {
                this.$emit('lesson-updated', newLesson);
            },
            openEditSlideModal(slide) {
                this.$emit('edit-slide', slide, this.textBlockIn.id);
            },
            addSlide() {
                this.$emit('add-slide', this.textBlockIn);
            },
            deleteTextBlock() {
                this.$emit('delete-text-block', this.textBlockIn);
            },
            openSlidePage(slide) {
                window.location.href = this.routes.lessonManualLessonShowSlidesAtSlide(this.lessonIn.id, slide.id);
            },
            deleteSlide(slide) {
                this.alert
                    .confirm(this.$t('presentation.slide.delete'), this.$t('presentation.slide.delete_explanation'))
                    .then((confirm) => {
                        if (confirm) {
                            this.alert.saving();

                            this.axios.delete(this.routes.lessonDeleteSlide(this.lessonIn.id, this.textBlockIn.id, slide.id)).then(
                                (response) => {
                                    this.$emit('lesson-updated', response.data);
                                    this.alert.success();
                                },
                                (error) => {
                                    return this.alert.ajaxError(error);
                                }
                            );
                        }
                    });
            },
            dynamicClass() {
                switch (this.textBlockIn.type) {
                    case 'learning-activity':
                        return ['learning-activity-block'];
                    case 'background':
                        return ['background-block'];
                    case 'extra':
                        return ['extra-block'];
                    case 'title-only':
                        return ['title-only-block'];
                    default:
                        return ['standard-block'];
                }
            },
        }
    };
</script>

<style scoped lang="scss">
    .text-block {
        position: relative;
        margin-bottom: $grid-gutter-width;
        border: none;

        &.edit-modus {
            border: 1px solid $border-color;

            + text-block {
                border-top: 1px solid $border-color;
            }
        }
    }

    .duration {
        position: relative;
        column-gap: map-get($grid-gutter-spacing, 'five');
        display: inline-flex;
        justify-content: center;
        align-items: center;
        line-height: 1;
        background-color: var(--intermediateColor, darken($primary, 20%));
        color: $white;
        font-family: $headings-font-family;
        font-weight: $font-weight-bold;
        padding: map-get($grid-gutter-spacing, 'eight');
        font-size: 1.125rem;
        left: 0;
    }

    .text-block-header {
        gap: map-get($grid-gutter-spacing, 'ten') map-get($grid-gutter-spacing, 'fifteen');
        margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
        flex-wrap: wrap;

        &,
        .text-block-title-wrapper,
        .text-block-actions {
            display: flex;
            align-items: center;
        }

        .btn {
            font-size: $base-font-size;
            line-height: 1.5;
        }

        .btn-drag-icon,
        .text-block-actions {
            align-self: flex-start;
        }
    }

    .btn-drag-icon {
        padding: 0;
        margin-right: map-get($grid-gutter-spacing, 'eight');
    }

    .text-block-title {
        margin-bottom: 0;
        font-weight: $font-weight-medium;
    }

    .text-block-actions {
        margin-left: auto;
        flex: 1 0 100%;
        justify-content: flex-end;
    }

    .content,
    .background-block,
    .extra-block {
        padding: map-get($grid-gutter-spacing, 'fifteen');
    }

    .background-block {
        background-color: var(--backgroundInfoTextBlockColor, lightslategray);
        margin-top: $grid-gutter-width;

        &.edit-modus {
            border: 1px solid var(--backgroundInfoTextBlockColor, lightslategray);;

            + text-block {
                border-top: 1px solid $border-color;
            }
        }
    }

    .extra-block {
        background-color: var(--extraTextBlockColor, lightyellow);
        margin-top: $grid-gutter-width;

        &.edit-modus {
            border: 1px solid var(--extraTextBlockColor, lightyellow);;

            + text-block {
                border-top: 1px solid $border-color;
            }
        }
    }

    .learning-activity-block {
        background-color: var(--learningActivityTextBlockColor, lightyellow);
        margin-top: $grid-gutter-width;

        &.edit-modus {
            border: 1px solid var(--learningActivityTextBlockColor, lightyellow);;

            + text-block {
                border-top: 1px solid $border-color;
            }
        }
    }

    .slide {
        padding: map-get($grid-gutter-spacing, 'twenty-one');
        background-color: var(--intermediateLighterColor);
        border: none;
        border-radius: $border-radius-content;

        .header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: map-get($grid-gutter-spacing, 'fifteen') $grid-gutter-width;
            margin-bottom: $grid-gutter-width;
        }

        .actions {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            margin-left: auto;
            gap: map-get($grid-gutter-spacing, 'ten') map-get($grid-gutter-spacing, 'fifteen');

            button {
                &:before {
                    margin-right: 0;
                    width: auto;
                }
            }
        }

        &:not(:last-child) {
            margin-bottom: $grid-gutter-width;
        }

        &:hover {
            box-shadow: 1px 0 4px rgba(124, 119, 221, 35%);
        }
    }

    .slide-count {
        color: $text-muted-color;
        font-weight: $font-weight-medium;
        font-size: 0.75rem;
        margin-top: map-get($grid-gutter-spacing, 'twenty-one');
    }

    @media (min-width: map-get($grid-breakpoints, 'md')) {
        .text-block-title-wrapper {
            flex: 1;
        }

        .text-block-actions {
            flex: 0 auto;
        }
    }

    @media (min-width: map-get($grid-breakpoints, 'xxl')) {
        .duration {
            position: absolute;
            flex-direction: column;
            left: -65px;
            top: 13px;
        }

        .slide {
            width: 55%;
            margin: 0 auto;
        }
    }
</style>
